/** @jsxRuntime classic */ // <-- remove this when TS updated to 4.1
/** @jsx jsx */ // <-- remove this when TS updated to 4.1
import { jsx } from '@emotion/core';
import { KeyboardEvent, ReactNode, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Text } from '../..';
import { Eye, EyeSlash } from 'phosphor-react';
import { useTheme } from 'emotion-theming';
import { isIOS, isSafari } from '../../../utils';
import { ThemeProps } from '../../../types';

export type InputTypes = 'email' | 'password' | 'text';
export interface InputProps {
  /**
   * Add any additional messages to the user e.g. a success message
   */
  additionalMessage?: ReactNode;
  /**
   * Label for optional button
   */
  buttonLabel?: string;
  /**
   * The css class for the input. Handled internally by emotion
   */
  className?: string;
  /**
   * The default value of the input
   */
  defaultValue?: string;
  /**
   * Disables the input and button
   */
  disabled?: boolean;
  /**
   * Disables the submit event
   */
  disabledSubmit?: boolean;
  /**
   * The error message to display
   */
  errorMessage?: string;
  /**
   * Id of the input
   */
  id: string;
  /**
   * Displays the loading animation
   */
  isBusy?: boolean;
  /**
   * Sigalises an error has occured
   */
  isError?: boolean;
  /**
   * Label for the input
   */
  label?: string;
  /**
   * Name of the input
   */
  name: string;
  /**
   * Change event handler
   */
  onChange?: (args?: any) => void;
  /**
   * Submit handler for optional button
   */
  onSubmit?: (args?: any) => void;  
  /**
   * OnKeyUp handler for optional button
   */
  onKeyUp?: (args?: any) => void;
  /**
   * The placeholder for the input
   */
  placeholder?: string;
  /**
   * The text below the label. Useful for providing additional
   * information on how to use the input
   */
  sublabel?: ReactNode;
  /**
   * The type of input
   */
  type: InputTypes;
  /**
   * Input value
   */
  value: string;
  /**
   * Add optional button to input
   */
  withButton?: boolean;
}

const Input = ({
  additionalMessage,
  buttonLabel,
  className,
  defaultValue,
  disabled,
  disabledSubmit,
  errorMessage,
  id,
  isBusy,
  isError,
  label,
  name,
  onChange,
  onSubmit,
  onKeyUp,
  placeholder,
  sublabel,
  type,
  value,
  withButton = false,
}: InputProps) => {
  const theme: ThemeProps = useTheme();
  const [isRevealed, setRevealed] = useState<boolean>(false);
  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !disabledSubmit && onSubmit) {
      !disabledSubmit && onSubmit && onSubmit();
    }
  };

  return (
    <div className={className}>
      <label htmlFor={id}>{label}</label>
      {sublabel && sublabel}
      <div className='input-container'>
        <input
          defaultValue={defaultValue}
          disabled={disabled}
          id={id}
          name={name}
          onChange={onChange}
          onKeyPress={(event: KeyboardEvent<HTMLInputElement>) =>
            handleKeyPress(event)
          }
          placeholder={placeholder}
          type={isRevealed ? 'text' : type}
          value={value}
        />
        {type === 'password' && (
          <Button
            className='reveal-button'
            onClick={() => setRevealed(!isRevealed)}
            onKeyUp={(e: any) => {
              if (e.key === 'Enter') {
                setRevealed(!isRevealed)
              }
            }}
            variant='secondary'>
            {isRevealed ? (
              <EyeSlash
                color={theme.colors.text.accent}
                size={24}
                weight='bold'
              />
            ) : (
              <Eye color={theme.colors.text.accent} size={24} weight='bold' />
            )}
          </Button>
        )}
        {withButton && (
          <Button
            disabled={disabled || disabledSubmit}
            isBusy={isBusy}
            onClick={onSubmit}
            onKeyUp={onKeyUp}
            variant='withInput'>
            {buttonLabel}
          </Button>
        )}
      </div>

      {errorMessage && (
        <Text as='span' color='error' weight='light'>
          {errorMessage}
        </Text>
      )}
      {additionalMessage && additionalMessage}
    </div>
  );
};

const StyledInput = styled((props: any) => <Input {...props} />)`
  ${({
    errorMessage,
    isError,
    theme,
    type,
    withButton,
  }: {
    errorMessage: string;
    isError: boolean;
    theme: ThemeProps;
    type: InputTypes;
    withButton: boolean;
  }) => {
    const returnOffset = () => {
      if (isIOS || isSafari) {
        return isError || errorMessage ? 0 : '-4px';
      } else {
        const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

        return isFirefox ? 0 : isError || errorMessage ? 2 : 0;
      }
    };

    return {
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      marginBottom: 20,
      width: '100%',
      button: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: theme.radii.inputs,
        borderTopLeftRadius: 0,
        borderTopRightRadius: theme.radii.inputs,
        height: 44,
        minWidth: 'auto',
        padding: '6px 16px',
      },
      '.reveal-button': {
        backgroundColor: 'transparent',
        borderColor: theme.colors.common.grey,
        borderWidth: 1,
        borderLeftWidth: 0,
        height: 44,
        right: 2,
      },
      div: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
      },
      input: {
        appearance: 'none',
        borderColor:
          isError || errorMessage
            ? theme.colors.error
            : theme.colors.common.grey,
        borderStyle: 'solid',
        borderBottomLeftRadius: theme.radii.inputs,
        borderBottomRightRadius:
          type === 'password' || withButton ? 0 : theme.radii.inputs,
        borderTopLeftRadius: theme.radii.inputs,
        borderTopRightRadius:
          type === 'password' || withButton ? 0 : theme.radii.inputs,
        borderWidth: isError || errorMessage ? 2 : 1,
        boxShadow: 'none',
        fontSize: 16,
        height: 44,
        outlineOffset: returnOffset(), //weirdness with iOS safari and outline (shadow) extending beyond its dimensions
        outlineWidth: 2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        padding: '6px 12px',
        whiteSpace: 'nowrap',
        width: '100%',
        '&::placeholder': {
          color: theme.colors.text.muted,
          fontSize: 14,
          [theme.mq.desktop]: {
            fontSize: 16,
          },
        },
      },
      '.input-container': {
        position: 'relative',
      },
      label: {
        fontSize: 16,
        marginBottom: 10,
      },
      span: {
        fontSize: 14,
        fontStyle: 'italic',
        marginTop: 5,
      },
    };
  }}
`;

export default StyledInput;
